// 3rd party
import React, { ReactElement } from 'react';
import styled, { useTheme } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

// Types
import { InputChangeHandler } from 'types';

// Components
import { InputLabel } from 'components';

// Component properties
interface Props {
  id?: string;
  width?: number; // give as pixel value
  height?: number; // give as pixel value
  checked?: boolean;
  onChange?: InputChangeHandler;
  label?: string;
  labelWeight?: number;
  disabled?: boolean;
  margin?: string;
  error?: boolean;
  fontSize?: number;
  className?: string;
}

// Styling
const Root = styled.p<{ margin?: string }>`
  position: relative;
  width: auto;
  height: auto;
  display: flex;
  align-items: center;
  align-self: center;
  margin: ${({ margin }) => margin};
`;

export const Input = styled.input<Props>`
  min-width: ${(props) => (props.width ? `${props.width / 16}rem` : '1.5625rem')};
  height: ${(props) => (props.height ? `${props.height / 16}rem` : '1.5625rem')};
  appearance: none;
  background-color: ${({ disabled, theme }) =>
    disabled ? theme.colors.field.disabled.fill : 'white'};
  border-radius: 4px;
  border: ${(props) => props.theme.colors.borders.border01.border};
  margin: 0;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &[type='checkbox']:checked {
    background-color: ${(props) => props.theme.colors.primaryBlue5};
    border-color: ${(props) => props.theme.colors.primaryBlue5};
  }

  &[type='checkbox']:disabled {
    background-color: ${(props) => props.theme.colors.lightGrey1};
    border-color: ${(props) => props.theme.colors.tableborder};
  }

  &[type='checkbox']:disabled:checked {
    background-color: ${(props) => props.theme.colors.tableborder};
    border-color: transparent;
  }

  &[type='checkbox']:hover:not(:checked):not(:disabled) {
    transition: 0.3s;
    background-color: ${(props) => props.theme.colors.primaryBlue5};
    border-color: ${(props) => props.theme.colors.primaryBlue5};
    box-shadow: inset 0 0 0 0.125rem white;
  }

  &[type='checkbox']:focus {
    box-shadow: 0px 0px 1px 1.5px rgba(0, 118, 204, 0.2);
  }

  &.error {
    border-color: ${(props) => props.theme.colors.validations.error.primary.fill};
  }
`;

export const CheckIcon = styled.p<Props>`
  pointer-events: none;
  width: ${(props) => (props.width ? `${props.width / 16}rem` : '1.5625rem')};
  height: ${(props) => (props.height ? `${props.height / 16}rem` : '1.5625rem')};
  position: absolute;
  color: ${(props) => (props.checked ? 'white' : 'transparent')};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${(props) => (props.width ? `${props.width / 32}rem` : '0.78125rem')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  svg {
    height: 12px;
  }
`;

const LabelContainer = styled.div<Props>`
  margin-left: 0.5rem;
  & > label {
    margin-bottom: 0;
    font-size: ${(props) => (props.fontSize ? `${props.fontSize / 16}rem` : `0.75rem`)};
    font-weight: ${(props) => (props.labelWeight ? props.labelWeight : 400)};
    width: 12rem;
    whitespace: nowrap;
    overflow: hidden;
    textoverflow: ellipsis;
  }
`;

const Checkbox = ({
  id,
  checked,
  onChange,
  width,
  height,
  label,
  labelWeight,
  disabled,
  margin,
  error,
  fontSize,
  className
}: Props): ReactElement => {
  const theme = useTheme();

  className = className ? `checkbox-component ${className}` : 'checkbox-component';

  return (
    <Root style={{ margin: margin }} {...{ className }}>
      <Input
        type="checkbox"
        id={`checkbox-${id}`}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className={error ? 'error' : ''}
        width={width}
        height={height}
      ></Input>
      <CheckIcon width={width} height={height} checked={checked} disabled={disabled}>
        <FontAwesomeIcon icon={faCheck} />
      </CheckIcon>
      {label && (
        <LabelContainer labelWeight={labelWeight} disabled={disabled} fontSize={fontSize}>
          <InputLabel
            color={
              disabled
                ? theme.colors.mediumGrey4
                : error
                ? theme.colors.validations.error.primary.fill
                : theme.colors.textLightBlack
            }
          >
            <label htmlFor={`checkbox-${id}`}>{label}</label>
          </InputLabel>
        </LabelContainer>
      )}
    </Root>
  );
};

export default Checkbox;
